import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { AlertCircle, Briefcase, TrendingUp, FileText, Users, Mail, CheckCircle2 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AdminLayout from './admin/AdminLayout';
import HoldingsTab from './admin/HoldingsTab';
import PerformanceTab from './admin/PerformanceTab';
import LettersTab from './admin/LettersTab';
import InvestorsTab from './admin/InvestorsTab';
import CommunicationsTab from './admin/CommunicationsTab';
import './admin/admin.css';

const SECTIONS = [
  { id: 'holdings', label: 'Holdings', icon: Briefcase, title: 'Holdings', subtitle: 'Positions, cash and live-computed totals' },
  { id: 'performance', label: 'Performance', icon: TrendingUp, title: 'Performance', subtitle: 'Monthly fund value history' },
  { id: 'letters', label: 'Letters', icon: FileText, title: 'Letters & reports', subtitle: 'Investor updates and annual reports' },
  { id: 'investors', label: 'Investors', icon: Users, title: 'Investors', subtitle: 'Access control and contributed capital' },
  { id: 'communications', label: 'Communications', icon: Mail, title: 'Communications', subtitle: 'Branded email updates to investors' },
];

const THEME_KEY = 'rgnr_admin_theme';

function FundAdmin() {
  const { isAuthenticated, isLoading, user, isFundAdmin } = useAuth();
  const [activeId, setActiveId] = useState('holdings');
  const [commDraft, setCommDraft] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);

  const handleStatus = useCallback((message, isError = false) => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, message, isError }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, isError ? 6000 : 3500);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  }, []);

  // Keep the document background in sync while in the admin area.
  useEffect(() => {
    document.body.dataset.adminTheme = theme;
    return () => {
      delete document.body.dataset.adminTheme;
    };
  }, [theme]);

  if (isLoading) {
    return (
      <div className="admin-denied">
        <LoadingSpinner text="Loading admin panel..." />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin" replace />;
  }

  if (!isFundAdmin) {
    return (
      <div className="admin-shell" data-theme={theme}>
        <div className="admin-denied">
          <div className="admin-denied-card">
            <AlertCircle size={44} color="var(--a-neg)" />
            <h2>Access denied</h2>
            <p>You do not have permission to access the Fund Admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  const section = SECTIONS.find((s) => s.id === activeId) || SECTIONS[0];

  return (
    <AdminLayout
      nav={SECTIONS}
      activeId={activeId}
      onSelect={setActiveId}
      theme={theme}
      onToggleTheme={toggleTheme}
      title={section.title}
      subtitle={section.subtitle}
    >
      {activeId === 'holdings' && <HoldingsTab onStatus={handleStatus} />}
      {activeId === 'performance' && <PerformanceTab onStatus={handleStatus} />}
      {activeId === 'letters' && (
        <LettersTab
          onStatus={handleStatus}
          onNotifyLetter={(draft) => {
            setCommDraft(draft);
            setActiveId('communications');
          }}
        />
      )}
      {activeId === 'investors' && <InvestorsTab onStatus={handleStatus} currentEmail={user.email} />}
      {activeId === 'communications' && (
        <CommunicationsTab
          onStatus={handleStatus}
          initialDraft={commDraft}
          onDraftConsumed={() => setCommDraft(null)}
        />
      )}

      <div className="a-toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`a-toast ${t.isError ? 'is-error' : 'is-success'}`}>
            {t.isError ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

export default FundAdmin;
